export type SignatureMode = 'draw' | 'type' | 'upload';

export class SignatureEngine {
  private canvas: HTMLCanvasElement | null = null;
  private ctx: CanvasRenderingContext2D | null = null;
  private isDrawing = false;
  private lastX = 0;
  private lastY = 0;
  private strokeColor = '#1a1a2e';
  private strokeWidth = 2.5;

  initialize(canvas: HTMLCanvasElement) {
    this.canvas = canvas;
    this.ctx = canvas.getContext('2d')!;
    this.ctx.lineCap = 'round';
    this.ctx.lineJoin = 'round';
    this.ctx.strokeStyle = this.strokeColor;
    this.ctx.lineWidth = this.strokeWidth;

    this.setupDrawingEvents();
  }

  private setupDrawingEvents() {
    if (!this.canvas) return;

    const getPos = (e: MouseEvent | TouchEvent) => {
      const rect = this.canvas!.getBoundingClientRect();
      if (e instanceof TouchEvent) {
        return {
          x: e.touches[0].clientX - rect.left,
          y: e.touches[0].clientY - rect.top,
        };
      }
      return {
        x: e.clientX - rect.left,
        y: e.clientY - rect.top,
      };
    };

    const startDraw = (e: MouseEvent | TouchEvent) => {
      e.preventDefault();
      this.isDrawing = true;
      const pos = getPos(e);
      this.lastX = pos.x;
      this.lastY = pos.y;
    };

    const draw = (e: MouseEvent | TouchEvent) => {
      if (!this.isDrawing || !this.ctx) return;
      e.preventDefault();
      const pos = getPos(e);

      this.ctx.beginPath();
      this.ctx.moveTo(this.lastX, this.lastY);
      this.ctx.lineTo(pos.x, pos.y);
      this.ctx.stroke();

      this.lastX = pos.x;
      this.lastY = pos.y;
    };

    const stopDraw = () => {
      this.isDrawing = false;
    };

    this.canvas.addEventListener('mousedown', startDraw);
    this.canvas.addEventListener('mousemove', draw);
    this.canvas.addEventListener('mouseup', stopDraw);
    this.canvas.addEventListener('mouseleave', stopDraw);
    this.canvas.addEventListener('touchstart', startDraw, { passive: false });
    this.canvas.addEventListener('touchmove', draw, { passive: false });
    this.canvas.addEventListener('touchend', stopDraw);
  }

  /**
   * Generate a typed signature using canvas text rendering
   */
  generateTypedSignature(
    text: string,
    font: string = 'italic 36px "Dancing Script", cursive',
    color: string = '#1a1a2e'
  ): string {
    const tempCanvas = document.createElement('canvas');
    tempCanvas.width = 400;
    tempCanvas.height = 100;
    const tempCtx = tempCanvas.getContext('2d')!;

    tempCtx.fillStyle = 'transparent';
    tempCtx.fillRect(0, 0, 400, 100);

    tempCtx.font = font;
    tempCtx.fillStyle = color;
    tempCtx.textBaseline = 'middle';
    tempCtx.fillText(text, 20, 50);

    return tempCanvas.toDataURL('image/png');
  }

  setStrokeColor(color: string) {
    this.strokeColor = color;
    if (this.ctx) {
      this.ctx.strokeStyle = color;
    }
  }

  setStrokeWidth(width: number) {
    this.strokeWidth = width;
    if (this.ctx) {
      this.ctx.lineWidth = width;
    }
  }

  clear() {
    if (!this.canvas || !this.ctx) return;
    this.ctx.clearRect(0, 0, this.canvas.width, this.canvas.height);
  }

  isEmpty(): boolean {
    if (!this.canvas || !this.ctx) return true;
    const imageData = this.ctx.getImageData(0, 0, this.canvas.width, this.canvas.height);
    return !imageData.data.some((channel, i) => i % 4 === 3 && channel !== 0);
  }

  toDataURL(format: 'image/png' | 'image/jpeg' = 'image/png'): string {
    if (!this.canvas) return '';
    return this.canvas.toDataURL(format);
  }

  toBlob(): Promise<Blob | null> {
    return new Promise((resolve) => {
      if (!this.canvas) {
        resolve(null);
        return;
      }
      this.canvas.toBlob(resolve, 'image/png');
    });
  }

  destroy() {
    this.canvas = null;
    this.ctx = null;
  }
}
